/**
 * Component for displaying a group of apellyasiyas for one exam
 * Modular component to keep AssignmentList clean
 */

import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ExamApellyasiyaGroup as ExamGroup } from '../../utils/apellyasiyaGrouping';
import { YoklaAssignment } from '../../types';

const GroupCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  border: 2px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #f59e0b;
  }
`;

const GroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f1f5f9;
`;

const GroupTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ExamName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const ExamType = styled.span`
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
`;

const GroupStats = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const ProgressText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
`;

const ProgressBar = styled.div<{ percentage: number }>`
  width: 120px;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.percentage}%;
    background: ${props => props.percentage === 100 ? '#10b981' : '#f59e0b'};
    transition: width 0.2s ease;
  }
`;

const AppealsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AppealItem = styled.div<{ $isCompleted: boolean }>`
  padding: 12px;
  background: ${props => props.$isCompleted ? '#f0fdf4' : '#fffbeb'};
  border: 1px solid ${props => props.$isCompleted ? '#86efac' : '#fde68a'};
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AppealInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AppealText = styled.span`
  font-size: 14px;
  color: #1e293b;
  font-weight: 500;
`;

const AppealDetails = styled.span`
  font-size: 12px;
  color: #64748b;
`;

const StatusBadge = styled.span<{ $isCompleted: boolean }>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.$isCompleted ? '#10b981' : '#f59e0b'};
  color: white;
`;

interface ExamApellyasiyaGroupProps {
  group: ExamGroup;
  onGroupClick: (collectionName: string) => void;
  onAppealClick: (appeal: YoklaAssignment) => void;
}

export const ExamApellyasiyaGroupCard: React.FC<ExamApellyasiyaGroupProps> = ({
  group,
  onGroupClick,
  onAppealClick,
}) => {
  return (
    <GroupCard onClick={() => onGroupClick(group.collectionName)}>
      <GroupHeader>
        <GroupTitle>
          <ExamName>{group.examName}</ExamName>
          <ExamType>{group.examType === 'buraxilis' ? 'Buraxılış İmtahanı' : 'Blok İmtahanı'}</ExamType>
        </GroupTitle>
        <GroupStats>
          <ProgressText>
            {group.completedCount}/{group.totalCount} tamamlandı
          </ProgressText>
          <ProgressBar percentage={group.progress} />
        </GroupStats>
      </GroupHeader>
      
      <AppealsList>
        {group.appeals.map((appeal) => {
          const isCompleted = appeal.status === 'completed';
          
          return (
            <AppealItem
              key={appeal.id}
              $isCompleted={isCompleted}
              onClick={(e) => {
                e.stopPropagation();
                onAppealClick(appeal);
              }}
            >
              <AppealInfo>
                <AppealText>
                  {appeal.fenn} - Sual {appeal.questionNumber?.replace(/^Q/i, '') || ''}
                </AppealText>
                <AppealDetails>
                  Sinif: {appeal.sinif || 'N/A'} {appeal.variant && `• Variant: ${appeal.variant}`}
                </AppealDetails>
              </AppealInfo>
              <StatusBadge $isCompleted={isCompleted}>
                {isCompleted ? 'Tamamlandı' : 'Gözləyir'}
              </StatusBadge>
            </AppealItem>
          );
        })}
      </AppealsList>
    </GroupCard>
  );
};
